class LiftHeroForm extends HTMLElement {
  #email = null;
  #btn = null;
  #fields = null;
  #error = null;
  #success = null;
  #controller = null;

  connectedCallback() {
    this.#email = this.querySelector('[data-form-email]');
    this.#btn = this.querySelector('[data-form-submit]');
    this.#fields = this.querySelector('[data-form-fields]');
    this.#error = this.querySelector('[data-form-error]');
    this.#success = this.querySelector('[data-form-success]');

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#btn?.addEventListener('click', () => this.#submit(), { signal });
    this.#email?.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Enter') this.#submit();
      },
      { signal },
    );
    this.#email?.addEventListener('input', () => this.#clearError(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #submit() {
    const email = this.#email?.value.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showError('Please enter a valid email address.');
      this.#shake(this.#email);
      this.#email?.focus();
      return;
    }

    const { listId, companyId } = this.dataset;

    if (!listId || !companyId) {
      console.warn('[LiftHeroForm] Klaviyo credentials missing — set List ID and Public API Key in section settings.');
      return;
    }

    this.#setLoading(true);
    this.#clearError();

    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Shopify prelaunch hero',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: listId },
              },
            },
          },
        }),
      });

      if (res.ok || res.status === 202) {
        this.#showSuccess();
      } else {
        const body = await res.json().catch(() => ({}));
        const msg = body.errors?.[0]?.detail ?? 'Something went wrong. Please try again.';
        this.#showError(msg);
        this.#setLoading(false);
      }
    } catch {
      this.#showError('Network error. Please try again.');
      this.#setLoading(false);
    }
  }

  #setLoading(on) {
    this.#btn.setAttribute('aria-busy', String(on));
    this.#btn.disabled = on;
    this.#email.disabled = on;
  }

  #showError(msg) {
    if (!this.#error) return;
    this.#error.textContent = msg;
    this.#error.classList.add('is-visible');
  }

  #clearError() {
    this.#error?.classList.remove('is-visible');
  }

  #showSuccess() {
    this.#fields?.classList.add('is-out');
    const success = this.#success;
    if (!success) return;
    success.removeAttribute('hidden');
    requestAnimationFrame(() => success.classList.add('is-in'));
  }

  #shake(el) {
    if (!el) return;
    el.classList.remove('is-shaking');
    void el.offsetWidth;
    el.classList.add('is-shaking');
    el.addEventListener('animationend', () => el.classList.remove('is-shaking'), { once: true });
  }
}

customElements.define('lift-hero-form', LiftHeroForm);
